import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import SocialButtons from '../components/SocialButtons';
import { SHARE_ITEMS } from '../components/CoachShares';
import { PRIMARY } from '../components/CaptureSheets';
import { kcal } from '../lib/format';
import { vnDate } from '../lib/coach';
import { useApp, useAppPath } from '../lib/AppContext';

const RECEIVES = ['Thực đơn tuần do AI lên theo mục tiêu', '20 lượt AI mỗi ngày để chụp món và gợi ý', 'Coach theo dõi và góp ý cho bạn'];
// A free connection (Kết nối miễn phí, for someone the coach already trains without the app).
const RECEIVES_FREE = ['Lịch tập với Coach, được nhắc trước giờ tập', 'Báo nghỉ hoặc xin đổi giờ ngay trong app', 'Coach theo dõi và góp ý cho bạn'];

function Shell({ children }) {
  return (
    <div data-theme="macro" className="min-h-dvh bg-base-200 text-base-content">
      <main className="pt-safe pb-safe mx-auto flex max-w-md flex-col gap-4 px-4 py-6">
        <p className="text-center font-display text-[22px]">Macro Mate</p>
        {children}
      </main>
    </div>
  );
}

// ⑦ ⑧ The page an invite link opens (/i/<token>): who invites, the gift and what the coach will see, before
// signing in; signing in (or up) brings the student back here to accept. Works on the website without the app.
export default function InvitePage() {
  const { api, signedIn, setProfile } = useApp();
  const path = useAppPath();
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [providers, setProviders] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/invites/${token}`).then(setInvite).catch((e) => setError(e.status === 404 ? 'not_found' : e.message));
    if (!signedIn) api.get('/auth/providers').then(setProviders).catch(() => setProviders({}));
  }, [api, token, signedIn]);

  // Hold the name question (NameSheet) while the invite is open: accepting fills in the coach's name for you.
  useEffect(() => {
    if (!signedIn) return undefined;
    setProfile((p) => (p ? { ...p, holdName: true } : p));
    return () => setProfile((p) => (p ? { ...p, holdName: false } : p));
  }, [signedIn, setProfile]);

  const accept = async (confirmSwitch = false) => {
    setBusy(true);
    setError(null);
    try {
      const data = await api.post(`/invites/${token}/accept`, { confirm_switch: confirmSwitch || undefined });
      // Joining a coach answers the role question (a student), so it isn't asked afterwards.
      setProfile((p) => (p ? { ...p, name: data.name, needsName: data.needs_name, needsRole: false } : p));
      setResult(data);
    } catch (e) {
      if (e.code === 'invite_unavailable') setInvite((i) => ({ ...i, status: 'expired' }));
      else if (e.code === 'has_coach') setInvite((i) => ({ ...i, has_coach: { name: e.data.current_coach_name } }));
      else setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (error === 'not_found' || (invite && invite.status !== 'pending' && !invite.accepted_by_you)) {
    return (
      <Shell>
        <section className="flex flex-col items-center gap-3 rounded-[24px] bg-base-100 p-6 text-center">
          <Icon name="clock" className="size-10 text-muted" />
          <p className="text-lg font-bold">Lời mời không còn dùng được</p>
          <p className="text-sm text-muted">Lời mời đã hết hạn, đã được dùng hoặc đã bị thu hồi. Nhắn PT gửi lại lời mời mới nhé.</p>
          {signedIn ? <Link to={path('/')} className="btn h-11 rounded-2xl">Về trang chủ</Link> : <a href="/login" className="btn h-11 rounded-2xl">Đăng nhập</a>}
        </section>
      </Shell>
    );
  }
  if (!invite) return <Shell>{error ? <p role="alert" className="text-sm text-error">{error}</p> : <div className="skeleton h-64 rounded-[24px]" />}</Shell>;

  const coach = invite.coach;
  // ⑧ accepted.
  if (result || invite.accepted_by_you) {
    const gift = result?.gift;
    const moved = result?.moved || 0;
    return (
      <Shell>
        <section className="flex flex-col items-center gap-3 rounded-[24px] bg-base-100 p-6 text-center">
          <span className="grid size-16 place-items-center rounded-full bg-[#e3f1e9] text-[#17804f]"><Icon name="check" className="size-8" /></span>
          <p className="text-xl font-extrabold">{invite.free ? `Đã kết nối với Coach ${coach.name}` : 'Bạn đã có Pro!'}</p>
          {!invite.free && (
            <p className="text-sm text-muted">
              Coach {coach.name} tặng {gift ? (gift.until_cancelled ? 'Pro đến khi Coach huỷ' : `Pro đến hết ngày ${vnDate(gift.ends_at)}`) : invite.gift.duration_label}.
            </p>
          )}
          {moved > 0 && <p className="text-sm text-muted">Coach {coach.name} đã hẹn bạn {moved} buổi tập. Bạn sẽ được nhắc trước giờ tập.</p>}
          {moved > 0 && <Link to={path('/schedule')} className={PRIMARY}>Xem lịch tập</Link>}
          {result?.targets?.calorie_goal && invite.preset_targets && (
            <p className="rounded-[14px] bg-base-200 px-4 py-2 text-sm">
              Mục tiêu Coach đặt: <b>{kcal(result.targets.calorie_goal)} kcal</b>{result.targets.protein_goal ? <> · <b>{result.targets.protein_goal} g đạm</b></> : null}
            </p>
          )}
          <Link to={path('/day')} className={moved > 0 ? 'btn btn-ghost h-11' : PRIMARY}>Ghi bữa ăn đầu tiên</Link>
        </section>
      </Shell>
    );
  }

  return (
    <Shell>
      <section className="flex flex-col gap-4 rounded-[24px] bg-base-100 p-5">
        <div className="flex items-center gap-3">
          <Avatar name={coach.name} url={coach.avatar_url} size={56} />
          <div className="min-w-0">
            <p className="text-sm text-muted">Huấn luyện viên</p>
            <p className="truncate text-xl font-bold">Coach {coach.name}</p>
            {coach.gym && <p className="text-sm text-muted">{coach.gym}</p>}
          </div>
        </div>
        {invite.free ? (
          <div className="rounded-[16px] bg-[#e3f1e9] p-4 text-[#17201b]">
            <p className="text-xs font-bold uppercase tracking-wide text-[#17804f]">Kết nối miễn phí</p>
            <p className="text-lg font-extrabold">Tập cùng Coach {coach.name} trên Macro Mate</p>
            <p className="text-sm">Bạn dùng gói Free, không phải trả gì.</p>
          </div>
        ) : (
          <div className="rounded-[16px] bg-[#f4c026] p-4 text-[#17201b]">
            <p className="text-xs font-bold uppercase tracking-wide">Quà tặng</p>
            <p className="text-2xl font-extrabold">{invite.gift.duration_label}</p>
            <p className="text-sm">Miễn phí, bạn không phải trả gì</p>
          </div>
        )}
        {invite.offline && (invite.offline.upcoming > 0 || invite.offline.past > 0) && (
          <p className="flex gap-2 rounded-[14px] bg-base-200 p-3 text-sm">
            <Icon name="calendar" className="mt-0.5 size-4 shrink-0 text-brand-text" />
            <span>
              Coach đã xếp {invite.offline.upcoming} buổi tập sắp tới{invite.offline.past > 0 ? ` và ghi ${invite.offline.past} buổi đã tập` : ''} cho bạn.
              Nhận lời để xem lịch trong app.
            </span>
          </p>
        )}
        <p className="rounded-[14px] bg-base-200 p-3 text-[15px] italic">“{invite.note || 'Cùng tập và ăn uống khoa học với mình trên Macro Mate nhé!'}”</p>
        <div>
          <p className="pb-1 text-sm font-bold">Bạn sẽ nhận được</p>
          <ul className="flex flex-col gap-1.5 text-sm">
            {(invite.free ? RECEIVES_FREE : RECEIVES).map((r) => <li key={r} className="flex gap-2"><Icon name="check" className="size-4 shrink-0 text-brand-text" /> {r}</li>)}
          </ul>
        </div>
        <div>
          <p className="pb-1 text-sm font-bold">Coach sẽ xem được</p>
          <ul className="flex flex-col gap-1.5 text-sm">
            {SHARE_ITEMS.map((s) => (
              <li key={s.key} className="flex gap-2">
                <Icon name={invite.shares[s.key] ? 'eye' : 'lock'} className="size-4 shrink-0 text-muted" />
                {s.label}{invite.shares[s.key] ? '' : ': không, trừ khi bạn bật'}
              </li>
            ))}
          </ul>
          <p className="pt-1 text-xs text-muted">Coach chỉ xem, không sửa được. Bạn đổi được trong Cài đặt bất cứ lúc nào.</p>
        </div>
      </section>

      {invite.own_invite && <p className="rounded-[20px] bg-[#fdf3d7] p-4 text-sm">Đây là lời mời bạn tạo, hãy gửi cho học viên.</p>}
      {signedIn && !invite.own_invite && (
        <div className="flex flex-col gap-3">
          {invite.personal_pro && (
            <p className="rounded-[16px] bg-base-100 p-3 text-sm">
              Bạn đang có Pro cá nhân. Gói đó không tự huỷ, bạn có thể <a className="font-semibold text-brand-text underline" href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noreferrer">quản lý trong App Store</a>.
            </p>
          )}
          {invite.has_coach ? (
            <>
              <p className="rounded-[16px] bg-base-100 p-3 text-sm">Bạn đang kết nối với Coach {invite.has_coach.name}. Nhận lời mời sẽ chuyển sang Coach {coach.name}; Coach {invite.has_coach.name} sẽ được báo.</p>
              <button type="button" className={PRIMARY} disabled={busy} onClick={() => accept(true)}>
                {busy && <span className="loading loading-spinner loading-sm" />} Chuyển sang Coach {coach.name}
              </button>
            </>
          ) : (
            <button type="button" className={PRIMARY} disabled={busy} onClick={() => accept()}>
              {busy && <span className="loading loading-spinner loading-sm" />} {invite.free ? `Kết nối với Coach ${coach.name}` : `Nhận ${invite.gift.duration_label}`}
            </button>
          )}
        </div>
      )}
      {!signedIn && (
        <section className="flex flex-col gap-3 rounded-[24px] bg-base-100 p-5">
          <p className="text-center font-bold">Đăng nhập hoặc tạo tài khoản để nhận</p>
          {providers && <SocialButtons providers={providers} onDone={() => { window.location.href = `/me/i/${token}`; }} />}
          <a href="/login" className="text-center text-sm font-medium text-muted underline">Đăng nhập bằng email / số điện thoại</a>
        </section>
      )}
      {error && <p role="alert" className="text-center text-sm font-medium text-error">{error}</p>}
    </Shell>
  );
}
